import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ApiError } from "@/lib/api";
import { fetchBrandTool, updateBrandTool } from "@/lib/brand-tool-settings";
import {
  JourneyApiError,
  listJourneys,
  type JourneyDefinition,
} from "@/lib/journey-api";

/** 下拉「不绑定」哨兵 */
const JOURNEY_NONE = "none";

type NewContactDefaultsPanelProps = {
  accountId: string;
  brandId: string;
  /**
   * 为 false 时不拉数（如 Dialog 关闭）。默认 true。
   * Settings 页常开；客户页弹层仅 open 时为 true。
   */
  active?: boolean;
  /** 控件 id 前缀，避免同页两处挂载时 id 冲突 */
  idPrefix?: string;
};

/**
 * 新客户默认：品牌级默认旅程。
 * Settings 页区块与客户页设置弹层共用，避免双份状态机。
 */
export function NewContactDefaultsPanel({
  accountId,
  brandId,
  active = true,
  idPrefix = "new-contact",
}: NewContactDefaultsPanelProps) {
  const [toolEnabled, setToolEnabled] = useState(false);
  const [defaultJourneyId, setDefaultJourneyId] = useState<string>(JOURNEY_NONE);
  const [journeys, setJourneys] = useState<JourneyDefinition[]>([]);
  const [savingJourney, setSavingJourney] = useState(false);
  const [journeyPrefsLoaded, setJourneyPrefsLoaded] = useState(false);

  const loadJourneyDefaults = useCallback(async () => {
    if (!accountId || !brandId) return;
    setJourneyPrefsLoaded(false);
    try {
      const [toolPayload, journeysPayload] = await Promise.all([
        fetchBrandTool(accountId, brandId, "journey"),
        listJourneys(accountId, brandId),
      ]);
      setToolEnabled(toolPayload.tool_enabled);
      const savedId = toolPayload.tool?.default_journey_id;
      setDefaultJourneyId(savedId != null ? String(savedId) : JOURNEY_NONE);
      setJourneys(journeysPayload.journeys ?? []);
      setJourneyPrefsLoaded(true);
    } catch (caught) {
      const message =
        caught instanceof JourneyApiError || caught instanceof ApiError
          ? caught.message
          : "无法加载默认旅程设置";
      toast.error(message);
    }
  }, [accountId, brandId]);

  useEffect(() => {
    if (!active) return;
    void loadJourneyDefaults();
  }, [active, loadJourneyDefaults]);

  async function saveDefaultJourney(nextValue: string | null) {
    if (!accountId || !brandId || savingJourney || !toolEnabled) return;
    const next = nextValue ?? JOURNEY_NONE;
    if (defaultJourneyId === next) return;
    setSavingJourney(true);
    const previous = defaultJourneyId;
    setDefaultJourneyId(next);
    try {
      const journeyId = next === JOURNEY_NONE ? null : Number(next);
      await updateBrandTool(accountId, brandId, "journey", {
        default_journey_id: journeyId,
      });
      toast.success(
        journeyId == null
          ? "已清除默认旅程"
          : "已保存默认旅程（新同步客户可据此自动绑定；现有客户请在资料里手动绑定）",
      );
    } catch (caught) {
      setDefaultJourneyId(previous);
      toast.error(caught instanceof ApiError ? caught.message : "无法保存默认旅程");
    } finally {
      setSavingJourney(false);
    }
  }

  const journeySelectId = `${idPrefix}-default-journey`;

  return (
    <div className="max-w-sm space-y-2">
      <Label htmlFor={journeySelectId}>默认旅程</Label>
      <Select
        value={defaultJourneyId}
        onValueChange={(v) => void saveDefaultJourney(v)}
        disabled={!journeyPrefsLoaded || !toolEnabled || savingJourney}
      >
        <SelectTrigger id={journeySelectId} aria-label="新客户默认旅程">
          <SelectValue placeholder="选择旅程" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={JOURNEY_NONE}>不绑定</SelectItem>
          {journeys
            .filter(
              (j) => j.status === "enabled" || String(j.id) === defaultJourneyId,
            )
            .map((journey) => (
              <SelectItem key={journey.id} value={String(journey.id)}>
                {journey.name}
                {journey.status !== "enabled" ? "（未启用）" : ""}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">
        {!toolEnabled
          ? "请先开通客户旅程工具后再设置。"
          : "仅可选本品牌已启用的旅程。自动绑定新客可后续接通；现有客户请在聊天右侧「旅程绑定」手动操作。"}
      </p>
    </div>
  );
}
